import { useEffect, useState } from 'react';
import { prefersReducedMotion } from './motionHelpers';

/**
 * Motion pieces for the sign-in page's brand panel. Each one holds still for people who ask for
 * reduced motion (they see the first phrase, the finished card and the final number).
 */

/** A phrase that changes every few seconds: the old one lifts away as the next rises in. */
export function RotatingWords({
  words,
  every = 2600,
  delay = 900,
  className = '',
}: {
  words: string[];
  every?: number;
  delay?: number;
  /** `is-dark` for light backgrounds (burgundy text instead of pale pink). */
  className?: string;
}) {
  const [index, setIndex] = useState(0);
  const [previous, setPrevious] = useState(-1);

  useEffect(() => {
    setIndex(0);
    setPrevious(-1);
    if (words.length < 2 || prefersReducedMotion()) return undefined;
    let timer: number | undefined;
    // Let the heading finish arriving before the first change.
    const start = window.setTimeout(() => {
      timer = window.setInterval(() => {
        setIndex((current) => {
          setPrevious(current);
          return (current + 1) % words.length;
        });
      }, every);
    }, delay);
    return () => {
      window.clearTimeout(start);
      window.clearInterval(timer);
    };
  }, [words, every, delay]);

  return (
    // Every phrase sits in the same grid cell, so the line keeps the widest one's width and
    // nothing jumps as they change.
    <span className={`join-rotator ${className}`} aria-hidden="true">
      {words.map((word, i) => (
        <span key={word} className={i === index ? 'is-active' : i === previous ? 'is-leaving' : ''}>
          {word}
        </span>
      ))}
    </span>
  );
}

export interface JourneyStage {
  icon: string;
  label: string;
}

const STAGE_MS = 1700;

/**
 * An example campaign moving through its stages, one tick at a time, then starting over: a quick
 * picture of what Twiva does for this side. Illustrative, so no real names or amounts.
 */
export function JourneyCard({ title, subtitle, stages }: { title: string; subtitle: string; stages: JourneyStage[] }) {
  // How many stages are done; one past the end holds the finished card for a moment. Each new
  // round (`round`) starts fresh instead of animating backwards.
  const [done, setDone] = useState(0);
  const [round, setRound] = useState(0);
  useEffect(() => {
    if (prefersReducedMotion()) {
      setDone(stages.length);
      return undefined;
    }
    setDone(0);
    let current = 0;
    const timer = window.setInterval(() => {
      current = current >= stages.length + 1 ? 0 : current + 1;
      if (current === 0) setRound((r) => r + 1);
      setDone(current);
    }, STAGE_MS);
    return () => window.clearInterval(timer);
  }, [stages]);

  const shown = Math.min(done, stages.length);
  const finished = shown === stages.length;
  return (
    <div className="join-float relative w-full max-w-sm rounded-2xl border border-background-50/15 bg-background-50/10 p-4 shadow-[0_20px_50px_rgba(20,0,10,0.35)] backdrop-blur-md">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-background-50">{title}</p>
          <p className="truncate text-xs text-background-50/65">{subtitle}</p>
        </div>
        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors duration-500 ${
            finished ? 'bg-accent-500 text-background-50' : 'bg-background-50/15 text-background-50/85'
          }`}
        >
          {finished ? 'Complete' : 'In progress'}
        </span>
      </div>

      <div key={round} className={round ? 'animate-fade-rise' : ''}>
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-background-50/15">
          <div
            className="h-full rounded-full bg-gradient-to-r from-primary-200 to-accent-400 transition-[width] duration-700 ease-out"
            style={{ width: `${(shown / stages.length) * 100}%` }}
          />
        </div>

        <ol className="mt-3 space-y-2">
          {stages.map((stage, i) => {
            const state = i < shown ? 'done' : i === shown ? 'current' : 'next';
            return (
              <li key={stage.label} className="join-stage flex items-center gap-2.5 text-sm" data-state={state}>
                <span className="join-stage-dot flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs">
                  <i className={state === 'done' ? 'ri-check-line' : stage.icon} aria-hidden="true" />
                </span>
                <span className="join-stage-label">{stage.label}</span>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
